import axios from "axios";
import { auth0 } from "@/auth";
import { getHostConfig } from "@/branding";

axios.defaults.withCredentials = true;
const goSolidApi = axios.create();

let loginRedirectInFlight = false;

// Silent renewal runs `/authorize?prompt=none` in a hidden iframe, which cannot show any UI.
// Auth0 answers with one of these whenever the renewal needs the user: no session
// (login_required), no recorded grant for this client/audience/scope (consent_required), or
// any other step it would have to prompt for (interaction_required). Only an interactive
// login clears them, and it doubles as the consent screen when consent is what's missing.
const INTERACTIVE_LOGIN_ERRORS = [
  "login_required",
  "consent_required",
  "interaction_required",
];

// Hostname decides the backend (see branding/index.ts). In development the
// base URL is empty so requests go through Vite's proxy (vite.config.ts,
// respects BACKEND_PORT).
goSolidApi.defaults.baseURL = getHostConfig().apiBase;

// Request interceptor to add the auth token
goSolidApi.interceptors.request.use(
  async (config) => {
    try {
      // Contrary to common sense, auth0.getAccessTokenSilently()
      // will throw an error if the user's session has expired.
      //
      // The "silent" part in the method name actually refers
      // to not creating a popup, not swallowing errors.
      const token = await auth0.getAccessTokenSilently();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    } catch (error: any) {
      // Without this, a failed renewal leaves the user on a blank page with a spinner, or on a
      // page whose every request fails, until they log out and back in by hand.
      //
      // This behaviour is only referenced in the underlying auth0-spa-js library
      // used by auth0-vue.
      //
      // Permalink to auth0-spa-js README:
      // https://github.com/auth0/auth0-spa-js/blob/8099701258d6c9b045e9e4e32af504de832f987b/README.md?plain=1#L83
      if (INTERACTIVE_LOGIN_ERRORS.includes(error?.error)) {
        if (!loginRedirectInFlight) {
          loginRedirectInFlight = true;
          try {
            await auth0.loginWithRedirect({
              appState: {
                target:
                  window.location.pathname +
                  window.location.search +
                  window.location.hash,
              },
            });
          } catch (redirectError) {
            loginRedirectInFlight = false;
            throw redirectError;
          }
        }
        // NOTE: never settle while the redirect unloads the page. Rethrowing
        // lets callers keep running: route guards would fall through to
        // a Dashboard redirect, whose authGuard fires a second
        // loginWithRedirect that overwrites the Auth0 transaction (and its
        // appState target) stored above.
        return new Promise(() => {});
      }
      throw error;
    }
  },
  (error) => {
    return Promise.reject(error);
  }
);

export { goSolidApi };
